import React, { useState } from "react";
import { CheckCircle, X } from "lucide-react";
import { useTranslation } from "react-i18next";

const API_URL = "api";

const ProductSelectionModal = ({ products, onContinue, onClose }) => {
    const [selectedProducts, setSelectedProducts] = useState([]);
    const { t } = useTranslation();

    const toggleProduct = (productId) => {
        setSelectedProducts((prev) =>
            prev.includes(productId)
                ? prev.filter((id) => id !== productId)
                : [...prev, productId]
        );
    };

    return (
        <div
            className="fixed inset-0 flex items-center justify-center 
            bg-black/60 backdrop-blur-sm z-[200] animate-fadeIn"
            onClick={onClose}
        >
            <div
                className="w-full max-w-4xl max-h-[90vh] overflow-y-auto relative
                bg-white/10 backdrop-blur-xl rounded-2xl p-8 border border-white/20
                shadow-[0_0_50px_rgba(255,0,255,0.25)] animate-scaleIn"
                onClick={(e) => e.stopPropagation()}
            >
                <button
                    className="absolute top-4 left-4 text-white hover:text-pink-300 transition"
                    onClick={onClose}
                >
                    <X className="w-7 h-7" />
                </button>

                <h2 className="text-3xl font-bold text-white text-center mb-2">
                    {t("customer.productSelection.title")}
                </h2>

                <p className="text-gray-200 text-center mb-6">
                    {t("customer.productSelection.subtitle")}
                </p>

                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-6">
                    {products.map((product) => (
                        <div
                            key={product.id}
                            onClick={() => toggleProduct(product.id)}
                            className={`p-6 rounded-xl cursor-pointer transition-all duration-300 
                            border backdrop-blur-lg shadow-xl
                            ${
                                selectedProducts.includes(product.id)
                                    ? "border-pink-500 bg-pink-500/20 shadow-[0_0_20px_rgba(255,0,255,0.3)]"
                                    : "border-white/20 bg-white/5 hover:bg-white/10 hover:border-pink-400"
                            }`}
                        >
                            <div className="text-center">
                                {product.productImage ? (
                                    <img
                                        src={`${API_URL.replace("/api", "")}${product.productImage}`}
                                        alt={product.productName}
                                        className="w-24 h-24 object-cover rounded-lg mx-auto mb-3 shadow-md"
                                    />
                                ) : (
                                    <div className="w-24 h-24 bg-white/10 rounded-lg mx-auto mb-3 flex items-center justify-center text-xs text-gray-300">
                                        {t("common.noImage")}
                                    </div>
                                )}

                                <h3 className="text-lg font-bold text-white mb-2">
                                    {product.productName}
                                </h3>

                                <p className="text-sm text-gray-300 line-clamp-2 mb-4">
                                    {product.description}
                                </p>

                                {selectedProducts.includes(product.id) && (
                                    <div className="flex items-center justify-center text-pink-300">
                                        <CheckCircle className="w-5 h-5 ml-2" />
                                        <span className="text-sm font-semibold">
                                            {t("customer.productSelection.selectedLabel")}
                                        </span>
                                    </div>
                                )}
                            </div>
                        </div>
                    ))}
                </div>

                <button
                    onClick={() => onContinue(selectedProducts)}
                    disabled={selectedProducts.length === 0}
                    className="w-full py-3 rounded-xl font-semibold 
                    bg-gradient-to-r from-pink-500 to-red-500 text-white
                    shadow-lg hover:opacity-90 transition disabled:opacity-40 disabled:cursor-not-allowed"
                >
                    {t("common.continueWithCount", {
                        count: selectedProducts.length,
                    })}
                </button>
            </div>

            <style>{`
                @keyframes fadeIn {
                    from { opacity: 0; }
                    to { opacity: 1; }
                }
                .animate-fadeIn {
                    animation: fadeIn .25s ease-out;
                }

                @keyframes scaleIn {
                    from { transform: scale(0.85); opacity: 0; }
                    to { transform: scale(1); opacity: 1; }
                }
                .animate-scaleIn {
                    animation: scaleIn .25s ease-out;
                }
            `}</style>
        </div>
    );
};

export default ProductSelectionModal;
